<template>
  <article
    class="plans-page"
    :class="{ 'plans-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="plans-title"
  >
    <div class="plans-layout">
      <aside class="plans-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="Plans and subscriptions sections">
            <a href="#our-future">Our future together</a>
            <a href="#choose-plan">Choose your plan</a>
            <a href="#business">For business</a>
            <a href="#privacy">Privacy for everyone</a>
            <a href="#community">A growing community</a>
            <a href="#affiliates">More than a subscription</a>
          </nav>
        </details>
      </aside>
      <div class="plans-body">
        <header>
          <p class="plans-eyebrow">
            Our future together
          </p>
          <h1 id="plans-title">
            Plans &amp; Subscriptions
          </h1>
          <p class="plans-lead">
            <strong>A little from each of us can build something substantial for all of us.</strong>
          </p>
          <p>Your subscription does more than preserve your biography. It helps finance the technology, storage, security, AI tools, and continued development that make Self Biography possible.</p>
          <p>Starting at just $2 a month, small contributions from a growing worldwide community can create the resources needed to support the platform and preserve our histories for future generations.</p>
          <p>Free to discover. Almost nothing to stay. More only when you want more.</p>
          <a
            class="plans-cta"
            href="#choose-plan"
          >Explore plans ↓</a>
        </header>
        <section
          class="plans-section"
          aria-labelledby="our-future"
        >
          <h2 id="our-future">
            Your subscription builds more than your biography
          </h2>
          <p>Together, we can build a platform that supports itself—and the community it serves.</p>
          <p>Self Biography was created with a different idea about how a social platform can finance its future.</p>
          <p>We are not asking people to subscribe simply to generate profits. We want the people who benefit from Self Biography to help make it possible.</p>
          <p>For an individual, $2 a month is very little. But when thousands—and eventually millions—of people participate, those small contributions can become a substantial source of funding.</p>
          <p>Together, subscriptions can help finance the infrastructure, storage, security, artificial intelligence, development, and long-term preservation necessary to keep Self Biography growing.</p>
          <p>Instead of depending primarily on advertising or turning people&#x27;s personal information into the product, we want to build a platform supported by the community itself.</p>
          <p>A little from each of us can build something substantial for all of us.</p>
          <p>Your subscription helps preserve your own life, but it also contributes to the technology that allows others to preserve theirs.</p>
          <p>That&#x27;s what we mean by our future together.</p>
        </section>
        <section
          class="plans-section"
          aria-labelledby="choose-plan"
        >
          <h2 id="choose-plan">
            Choose your plan
          </h2>
          <p class="plans-lead">
            Start free. Grow at your own pace.
          </p>
          <billing-plans embedded />
        </section>
        <section
          class="plans-section"
          aria-labelledby="business"
        >
          <p class="plans-eyebrow">Business Plus · $15/month</p>
          <h2 id="business">
            More space for your business and life’s work
          </h2>
          <p>A company has a biography, too.</p>
          <p>Businesses, studios, foundations, institutions, associations, and corporations all have histories worth preserving.</p>
          <p>Business Plus gives you 75 GB of storage and 1,000 AI credits each billing cycle to preserve professional history, business milestones, projects, artwork and large collections of photographs and video.</p>
          <p>Create a living archive that can continue growing as new people, achievements, and chapters become part of the organization&#x27;s history.</p>
          <p>That is three times the storage and twice the AI credits of Professional. Use the same biography, diary, media and privacy tools with more capacity for a growing archive.</p>
          <p class="plans-statement">
            Start with the space you need today. Upgrade as your archive grows.
          </p>
          <router-link to="/billing?offer=business_plus">
            Choose Business Plus →
          </router-link>
        </section>
        <section
          class="plans-section"
          aria-labelledby="privacy"
        >
          <h2 id="privacy">
            Privacy is not a premium feature
          </h2>
          <p>You are always the gatekeeper.</p>
          <p>Privacy should be fundamental to Self Biography, regardless of which subscription you choose.</p>
          <p>Every member should be able to decide what remains private, what is shared, and what becomes part of a public biography.</p>
          <p>Paid subscriptions are intended to provide more capacity, capabilities, and services—not to purchase the right to protect your memories.</p>
          <router-link to="/pages/privacy-policy">
            Read our Privacy Policy →
          </router-link>
        </section>
        <section
          class="plans-section"
          aria-labelledby="community"
        >
          <h2 id="community">
            A community that grows in value
          </h2>
          <p>Today, Self Biography is about preserving memories, experiences, accomplishments, and the lives of people who might otherwise be forgotten.</p>
          <p>As the community grows, it can also create opportunities for professional connections, creative collaboration, commerce, marketplaces, and even personal relationships.</p>
          <p>Knowing something about the person behind a profile can make a difference when buying something, working together, or establishing a relationship.</p>
          <p>A voluntarily shared biography can provide context that an ordinary username cannot.</p>
          <p>Our long-term vision is to build a community where people can discover one another through their histories and experiences, with privacy and individual choice remaining central.</p>
          <p>These possibilities are part of our future development vision, not current subscription benefits.</p>
        </section>
        <section
          class="plans-section"
          aria-labelledby="affiliates"
        >
          <h2 id="affiliates">
            More than a subscription
          </h2>
          <p>Become part of something bigger.</p>
          <p>Your subscription helps finance Self Biography.</p>
          <p>Your memories give it meaning.</p>
          <p>Your participation helps the community grow.</p>
          <p>And through our Affiliate Program, you may also have the opportunity to earn real-money commissions by introducing new paying subscribers.</p>
          <p>Subscribe to support the community. Become an Affiliate to help grow it—and participate in its success.</p>
          <router-link
            class="plans-cta"
            to="/pages/affiliate-program"
          >
            Explore the Affiliate Program →
          </router-link>
        </section>
        <footer class="plans-links">
          <router-link to="/pages/support?topic=Billing">
            Billing support →
          </router-link>
          <a href="#plans-title">Back to top ↑</a>
        </footer>
      </div>
    </div>
  </article>
</template>
<script>
import BillingPlans from '@/views/pages/pricing/BillingPlans.vue'

export default {
  name: 'PlansSubscriptions',
  components: { BillingPlans },
  data: () => ({ previousTitle: '' }),
  mounted() {
    this.previousTitle = document.title
    document.title = 'Plans & Subscriptions | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = ['plans-title', 'our-future', 'choose-plan', 'business', 'privacy', 'community', 'affiliates'].includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
}
</script>
<style lang="scss" scoped>
.plans-page {
  --plans-text: #5e5669; --plans-heading: #393345; --plans-accent: #713ac7; --plans-surface: #fff; --plans-tint: #f3eefb; --plans-divider: rgba(94,86,105,.16);
  max-width: 1160px; margin: 24px auto 48px; color: var(--plans-text); font-size: 16px; line-height: 1.8; text-align: left;
  &--dark { --plans-text: #d0cadf; --plans-heading: #eee9fa; --plans-accent: #c6a5ff; --plans-surface: #312d4b; --plans-tint: #39304f; --plans-divider: rgba(231,227,252,.16); }
  h1,h2,h3 { color: var(--plans-heading); font-weight: 600; line-height: 1.35; text-align: left; scroll-margin-top: 88px; }
  h1 { font-size: 16px; letter-spacing: -.035em; margin-bottom: 24px; }
  h2 { font-size: 16px; letter-spacing: -.02em; margin-bottom: 22px; }
  h3 { font-size: 16px; margin-bottom: 18px; }
  h3 span { color: var(--plans-accent); font-size: 1rem; }
  p { margin-bottom: 18px; }
  strong { font-weight: 600; }
  a { color: var(--plans-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--plans-accent); outline-offset: 4px; }
  .plans-eyebrow { color: var(--plans-accent); font-size: .75rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
}
.plans-layout { display: grid; grid-template-columns: 200px minmax(0,1fr); gap: 32px; align-items: start; }
.plans-index { position: sticky; top: 88px; }
.plans-index summary { font-weight: 600; cursor: pointer; min-height: 44px; }
.plans-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--plans-divider); padding-left: 16px; }
.plans-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: .875rem; line-height: 1.5; text-decoration: none; }
.plans-index a:hover { text-decoration: underline; }
.plans-body { min-width: 0; background: var(--plans-surface); border: 1px solid var(--plans-divider); border-radius: 8px; padding: 44px; }
.plans-lead { font-size: 16px; color: var(--plans-heading); }
.plans-section { margin-top: 30px; padding-top: 34px; border-top: 1px solid var(--plans-divider); }
.plans-statement { color: var(--plans-accent); padding: 20px 24px; background: var(--plans-tint); border-radius: 6px; margin: 24px 0; }
.plans-links { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--plans-divider); }
.plans-links a { display: inline-flex; align-items: center; min-height: 44px; font-size: .875rem; }
@media(max-width:959px) {
  .plans-layout { grid-template-columns: minmax(0,1fr); gap: 20px; }
  .plans-index { position: static; }
  .plans-index summary { padding: 8px 0; }
  .plans-body { padding: 28px; }
}
@media(max-width:599px) {
  .plans-page { margin: 0 auto 32px; }
  .plans-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--plans-divider); border-radius: 0; background: transparent; }
  .plans-statement { padding: 18px 20px; }
  .plans-links { flex-direction: column; gap: 4px; }
}

.plans-page ul { padding-left: 22px; margin-bottom: 24px; }
.plans-page li { padding-left: 4px; margin-bottom: 12px; }
.plans-page h3 { margin-top: 26px; }
.plans-page .plans-cta { display: inline-flex; align-items: center; min-height: 44px; padding: 10px 18px; border-radius: 6px; background: var(--plans-accent); color: var(--plans-surface); text-decoration: none; font-weight: 600; }
// Plan cards are rendered by a child component with Vuetify heading utilities.
.plans-page ::v-deep :is(h1, h2, h3, h4, h5, h6, .text-h1, .text-h2, .text-h3, .text-h4, .text-h5, .text-h6) {
  font-size: 16px !important;
}
</style>
